import supabase from "@/services/supabaseClient.js";
import {
  LEGAL_PRIVACY_VERSION,
  LEGAL_TERMS_VERSION,
} from "@/pages/legal/legalContent.js";

export { LEGAL_PRIVACY_VERSION, LEGAL_TERMS_VERSION };

const LEGAL_ACCEPTANCE_TIMEOUT_MS = 4500;
const ACCEPTANCE_CACHE_PREFIX = "afrimed:legal-acceptance";
const memoryAcceptanceCache = new Set();
const acceptanceRequests = new Map();

function acceptanceCacheKey(userId) {
  const uid = String(userId || "").trim();
  if (!uid) return "";
  return `${ACCEPTANCE_CACHE_PREFIX}:${uid}:${LEGAL_TERMS_VERSION}:${LEGAL_PRIVACY_VERSION}`;
}

function readAcceptanceCache(userId) {
  const key = acceptanceCacheKey(userId);
  if (!key) return false;
  if (memoryAcceptanceCache.has(key)) return true;
  if (typeof window === "undefined") return false;

  try {
    // A versioned acceptance is safe to retain across browser restarts. It is
    // only a first-paint hint: the gate still refreshes the durable record in
    // the background and immediately honours an authoritative mismatch.
    const raw =
      window.sessionStorage?.getItem(key) ||
      window.localStorage?.getItem(key);
    if (raw !== "accepted") return false;
    memoryAcceptanceCache.add(key);
    return true;
  } catch {
    return false;
  }
}

function writeAcceptanceCache(userId) {
  const key = acceptanceCacheKey(userId);
  if (!key) return;
  memoryAcceptanceCache.add(key);
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage?.setItem(key, "accepted");
    window.localStorage?.setItem(key, "accepted");
  } catch {
    // Cache is only a performance helper; DB acceptance remains authoritative.
  }
}

function clearAcceptanceCache(userId) {
  const key = acceptanceCacheKey(userId);
  if (!key) return;
  memoryAcceptanceCache.delete(key);
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage?.removeItem(key);
    window.localStorage?.removeItem(key);
  } catch {
    // Cache cleanup should never block the legal decision flow.
  }
}

function withTimeout(promise, timeoutMs, message) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => {
    clearTimeout(timeoutId);
  });
}

function cleanRoute(route = "") {
  const value = String(route || "").trim();
  if (!value.startsWith("/") || value.startsWith("//")) return "";
  return value.slice(0, 500);
}

function getUserAgent() {
  if (typeof navigator === "undefined") return "";
  return String(navigator.userAgent || "").trim().slice(0, 500);
}

export async function hasCurrentLegalAcceptance(userId, { bypassCache = false } = {}) {
  const uid = String(userId || "").trim();
  if (!uid) return false;
  if (!bypassCache && readAcceptanceCache(uid)) return true;

  const key = acceptanceCacheKey(uid);
  if (acceptanceRequests.has(key)) return acceptanceRequests.get(key);
  const request = (async () => {
    const { data, error } = await withTimeout(
      supabase
        .from("user_legal_acceptances")
        .select("id,accepted_at,declined_at,terms_version,privacy_version")
        .eq("user_id", uid)
        .eq("terms_version", LEGAL_TERMS_VERSION)
        .eq("privacy_version", LEGAL_PRIVACY_VERSION)
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .limit(1),
      LEGAL_ACCEPTANCE_TIMEOUT_MS,
      "AfriMed™ could not verify your Terms acceptance quickly enough. Please try again.",
    );
    if (error) throw error;
    const accepted = Boolean(data?.[0]?.accepted_at) && !data?.[0]?.declined_at;
    if (accepted) writeAcceptanceCache(uid);
    else clearAcceptanceCache(uid);
    return accepted;
  })();
  acceptanceRequests.set(key, request);
  try { return await request; }
  finally { if (acceptanceRequests.get(key) === request) acceptanceRequests.delete(key); }

}

export async function recordLegalAcceptance({
  userId,
  sourceRoute = "",
  accepted = true,
} = {}) {
  const uid = String(userId || "").trim();
  if (!uid) throw new Error("Sign in before accepting AfriMed legal terms.");

  const now = new Date().toISOString();
  const payload = {
    user_id: uid,
    terms_version: LEGAL_TERMS_VERSION,
    privacy_version: LEGAL_PRIVACY_VERSION,
    accepted_at: accepted ? now : null,
    declined_at: accepted ? null : now,
    source_route: cleanRoute(sourceRoute),
    user_agent: getUserAgent(),
  };

  const { data, error } = await supabase
    .from("user_legal_acceptances")
    .insert(payload)
    .select("id,user_id,terms_version,privacy_version,accepted_at,declined_at")
    .single();

  if (error) throw error;
  if (accepted) writeAcceptanceCache(uid);
  else clearAcceptanceCache(uid);
  return data;
}

export function hasCachedCurrentLegalAcceptance(userId) {
  return readAcceptanceCache(userId);
}
